/**
 * CSV verification hook for bulk Ring Group membership Add/Remove.
 *
 * Modelled on useCSVCreateUsers.js — parses the uploaded CSV, resolves each
 * row's Ring Group (by name) and member (by UPN) against the company's
 * current 8x8 platform users, and partitions rows into passed/failed for
 * VerifyCSVTableView. Rows that would be a no-op (adding an existing member,
 * removing a non-member) are marked with IGNORED_MESSAGE so they land in the
 * "Unchanged Records" section instead of triggering an API call.
 */
import React from 'react';
import Papa from 'papaparse';
import { useSelector } from 'react-redux';
import delay from 'lodash/delay';
import keyBy from 'lodash/keyBy';
import { downloadCSV } from '../../../../utils/utils';
import appConfig from '../../../../config.json';
import { PlatformEnums } from '../../../../utils/platforms';
import getCompanyPlatformUsers from '@/API/PlatformUser/getCompanyPlatformUsers';
import { getRingGroupMembers } from 'pages/services/eightByEight/userManagement/api';
import { IGNORED_MESSAGE, SUCCESS_MESSAGE } from './useCSVUsers';

export const CSV_TYPE = 'text/csv';
export const CSV_TYPE_FX = 'application/vnd.ms-excel';
export const CSV_TYPE_ERROR = 'File must be .csv type';

const REQUIRED_FIELD_ERROR =
    'Missing required field(s): Ring Group Name, Member UPN, and Action are required.';
const ACTION_INVALID_ERROR = 'Action must be either "Add" or "Remove".';
const RING_GROUP_DNE_ERROR = 'This Ring Group does not exist in your company.';
const RING_GROUP_AMBIGUOUS_ERROR =
    'Multiple Ring Groups share this name — cannot uniquely resolve it. Please rename one of the Ring Groups.';
const MEMBER_DNE_ERROR =
    'This Member UPN does not exist in your company as an 8x8 platform user.';
const MEMBER_NOT_ELIGIBLE_ERROR =
    'This member cannot be added — Ring Groups cannot contain other Ring Groups, and members must have a phone number assigned.';

const ADD = 'Add';
const REMOVE = 'Remove';

const INITIAL_CHECKS = [
    { label: 'Received CSV', check: false, current: true },
    { label: 'Fetching Ring Groups & Members', check: false, current: false },
    { label: 'Converting CSV Headers', check: false, current: false },
    { label: 'Verifying Required Fields', check: false, current: false },
    { label: 'Resolving Ring Groups & Members', check: false, current: false },
    { label: 'Checking Current Membership', check: false, current: false },
];

export const DEFAULT_VERIFICATION_RESULT = {
    passed: { columns: [], data: [] },
    failed: { columns: [], data: [] },
};

const FIELDS_TO_EXPORT = {
    ringGroupName: 'Ring Group Name',
    memberUPN: 'Member UPN',
    action: 'Action',
    errors: 'Errors',
};

// Keep the raw trimmed value here (rather than collapsing unrecognised
// values to '') so the required-field check and the action-validity check
// stay distinguishable — see toCanonicalAction for the Add/Remove mapping
// used once a row is known to have a valid action.
const normaliseAction = (value) => (value || '').trim();

const toCanonicalAction = (value) =>
    value.toLowerCase() === 'add' ? ADD : REMOVE;

const isValidAction = (value) =>
    ['add', 'remove'].includes(value.toLowerCase());

// Company platform user list may come back either as a flat array or as an
// object with a nested `platformUsers` array (see the same normalisation in
// RingGroupEditContent.jsx).
const normalisePlatformUserList = (raw) =>
    Array.isArray(raw) ? raw : raw?.platformUsers || [];

const getPlatformUserId = (user) => user?.id || user?.primaryUserID || '';

const getPlatformUserNumber = (user) =>
    user?.userProfiles?.[0]?.platformNumber?.telephoneNumber || '';

const isRingGroupUser = (user) => user?.userType === 'RingGroup';

/**
 * CSV Hook to handle validating uploaded Ring Group membership CSVs.
 */
export default function useCSVRingGroupMembership() {
    const [verificationChecks, setVerificationChecks] =
        React.useState(INITIAL_CHECKS);
    const [uploadData, setUploadData] = React.useState(null);
    const [payloads, setPayloads] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState('');
    const [csvLimit, setCSVLimit] = React.useState(false);
    const [verificationResult, setVerificationResult] = React.useState(
        DEFAULT_VERIFICATION_RESULT,
    );

    const { currentCompany } = useSelector((state) => state.navigationLists);
    const { requiredScope } = useSelector((state) => state.login);

    const markCheck = (i) => {
        delay(
            (index) => {
                setVerificationChecks((prev) => {
                    const next = [...prev];
                    if (next[index]) {
                        next[index].check = true;
                        if (index < next.length - 1)
                            next[index + 1].current = true;
                    }
                    return next;
                });
            },
            750,
            i,
        );
    };

    const handleReset = () => {
        setVerificationChecks(INITIAL_CHECKS.map((c) => ({ ...c })));
        setUploadData(null);
        setPayloads([]);
        setLoading(false);
        setError('');
        setCSVLimit(false);
        setVerificationResult(DEFAULT_VERIFICATION_RESULT);
    };

    const handleExportInvalidData = () => {
        const failed = verificationResult.failed.data;
        const rows = failed.map((row) =>
            Object.keys(FIELDS_TO_EXPORT).reduce((acc, key) => {
                if (key === 'errors' && Array.isArray(row[key])) {
                    acc[FIELDS_TO_EXPORT[key]] = row[key].join(' | ');
                } else {
                    acc[FIELDS_TO_EXPORT[key]] = row[key] ?? '';
                }
                return acc;
            }, {}),
        );
        const res = Papa.unparse(rows);
        downloadCSV(res, 'InvalidRingGroupMembershipData.csv');
    };

    const handleUploadUsersData = async (e) => {
        const file = e?.target?.files?.[0];
        if (!file) return;

        if (file.type !== CSV_TYPE && file.type !== CSV_TYPE_FX) {
            setError(CSV_TYPE_ERROR);
            return;
        }

        setLoading(true);
        setVerificationChecks(INITIAL_CHECKS.map((c) => ({ ...c })));

        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            complete: async (results) => {
                try {
                    if (results.data.length > appConfig.csvLimit) {
                        setCSVLimit(true);
                        setLoading(false);
                        return;
                    }

                    setUploadData(results.data);

                    // Step 0: Received CSV
                    markCheck(0);

                    // Step 1: Fetch the company's 8x8 platform users (Ring
                    // Groups + candidate members come from the same list —
                    // see getCompanyPlatformUsers usage in RingGroupEditContent).
                    const platformId = PlatformEnums.EIGHTBYEIGHT;
                    let platformUsers = [];
                    try {
                        const rawList = await getCompanyPlatformUsers(
                            currentCompany,
                            platformId,
                            requiredScope,
                        );
                        platformUsers = normalisePlatformUserList(rawList);
                    } catch (_err) {
                        // If this fails, every row will fail Ring Group /
                        // Member resolution below — surfaced per-row rather
                        // than as a hard stop, consistent with the Users flow.
                    }
                    markCheck(1);

                    // Step 2: Convert CSV headers to internal shape
                    const converted = results.data.map((row) => ({
                        ringGroupName: (row['Ring Group Name'] || '').trim(),
                        memberUPN: (row['Member UPN'] || '').trim(),
                        action: normaliseAction(row['Action']),
                    }));
                    markCheck(2);

                    // Step 3: Verify required fields
                    const afterRequiredCheck = converted.map((row) => {
                        const errors = [];
                        if (
                            !row.ringGroupName ||
                            !row.memberUPN ||
                            !row.action
                        ) {
                            errors.push(REQUIRED_FIELD_ERROR);
                        } else if (!isValidAction(row.action)) {
                            errors.push(ACTION_INVALID_ERROR);
                        }
                        // Normalise to the canonical 'Add'/'Remove' label once
                        // validity is confirmed, so every later comparison and
                        // the CSV/table display use a consistent value.
                        return {
                            ...row,
                            action:
                                errors.length === 0
                                    ? toCanonicalAction(row.action)
                                    : row.action,
                            errors,
                        };
                    });
                    markCheck(3);

                    // Step 4: Resolve Ring Group (by name) and Member (by UPN)
                    const ringGroups = platformUsers.filter(isRingGroupUser);
                    const ringGroupNameCounts = {};
                    ringGroups.forEach((rg) => {
                        const key = (rg.displayName || '').trim().toLowerCase();
                        if (!key) return;
                        ringGroupNameCounts[key] =
                            (ringGroupNameCounts[key] || 0) + 1;
                    });
                    const ringGroupsByName = keyBy(ringGroups, (rg) =>
                        (rg.displayName || '').trim().toLowerCase(),
                    );
                    const usersByUPN = keyBy(platformUsers, (u) =>
                        (u.userPrincipalName || u.email || '')
                            .trim()
                            .toLowerCase(),
                    );

                    const afterResolveCheck = afterRequiredCheck.map((row) => {
                        if (row.errors.length > 0) return row;

                        const rgKey = row.ringGroupName.toLowerCase();
                        const memberKey = row.memberUPN.toLowerCase();
                        const ringGroup = ringGroupsByName[rgKey];
                        const member = usersByUPN[memberKey];
                        const errors = [];

                        if (!ringGroup) {
                            errors.push(RING_GROUP_DNE_ERROR);
                        } else if (ringGroupNameCounts[rgKey] > 1) {
                            errors.push(RING_GROUP_AMBIGUOUS_ERROR);
                        }

                        if (!member) {
                            errors.push(MEMBER_DNE_ERROR);
                        } else if (
                            row.action === ADD &&
                            (isRingGroupUser(member) ||
                                !getPlatformUserNumber(member) ||
                                getPlatformUserId(member) ===
                                    getPlatformUserId(ringGroup))
                        ) {
                            errors.push(MEMBER_NOT_ELIGIBLE_ERROR);
                        }

                        if (errors.length > 0) return { ...row, errors };

                        return {
                            ...row,
                            errors,
                            ringGroupID: getPlatformUserId(ringGroup),
                            memberID: getPlatformUserId(member),
                        };
                    });
                    markCheck(4);

                    // Step 5: Check current membership for every Ring Group
                    // referenced by a resolvable row, so Add/Remove rows that
                    // would be a no-op can be flagged as unchanged.
                    const uniqueRingGroupIds = [
                        ...new Set(
                            afterResolveCheck
                                .filter((r) => r.errors.length === 0)
                                .map((r) => r.ringGroupID),
                        ),
                    ];
                    const membershipByRingGroupId = {};
                    await Promise.all(
                        uniqueRingGroupIds.map(async (ringGroupId) => {
                            try {
                                const membersRes = await getRingGroupMembers(
                                    ringGroupId,
                                    requiredScope,
                                );
                                const members = membersRes?.data || [];
                                membershipByRingGroupId[ringGroupId] = new Set(
                                    members
                                        .map(
                                            (m) =>
                                                m?.memberUserID ||
                                                m?.memberUser?.id ||
                                                m?.memberID ||
                                                m?.platformUserID ||
                                                m?.id ||
                                                '',
                                        )
                                        .filter(Boolean),
                                );
                            } catch (_err) {
                                membershipByRingGroupId[ringGroupId] =
                                    new Set();
                            }
                        }),
                    );
                    markCheck(5);

                    // Partition into passed / failed and build API payloads
                    const passed = [];
                    const failed = [];
                    const builtPayloads = [];

                    afterResolveCheck.forEach((row) => {
                        if (row.errors.length > 0) {
                            failed.push({ ...row });
                            return;
                        }

                        const currentMembers =
                            membershipByRingGroupId[row.ringGroupID] ||
                            new Set();
                        const isCurrentMember = currentMembers.has(
                            row.memberID,
                        );
                        const isNoOp =
                            (row.action === ADD && isCurrentMember) ||
                            (row.action === REMOVE && !isCurrentMember);

                        passed.push({
                            ...row,
                            verificationStatus: isNoOp
                                ? IGNORED_MESSAGE
                                : SUCCESS_MESSAGE,
                        });

                        if (!isNoOp) {
                            builtPayloads.push({
                                type:
                                    row.action === ADD
                                        ? 'addMember'
                                        : 'removeMember',
                                payload: {
                                    parentUserID: row.ringGroupID,
                                    memberUserID: row.memberID,
                                    voiceMailAccessEnabled: false,
                                },
                                meta: {
                                    ringGroupName: row.ringGroupName,
                                    memberUPN: row.memberUPN,
                                    action: row.action,
                                },
                                requiredScope,
                            });
                        }
                    });

                    setVerificationResult({
                        passed: { columns: [], data: passed },
                        failed: { columns: [], data: failed },
                    });
                    setPayloads(builtPayloads);
                } catch (err) {
                    setError(
                        err?.response?.data?.message ||
                            err?.message ||
                            'An error occurred while validating the CSV. Please try again.',
                    );
                }
                // Do NOT set loading=false here — loading stays true so the
                // validation-steps view remains visible until the user clicks
                // "View Results".
            },
            error: (err) => {
                setError(err?.message || 'Failed to parse CSV file.');
                setLoading(false);
            },
        });
    };

    return {
        handleUploadUsersData,
        uploadData,
        verificationChecks,
        loading,
        error,
        payloads,
        verificationResult,
        handleReset,
        handleExportInvalidData,
        csvLimit,
    };
}
